import { isEqual } from 'lodash';
import moment from 'moment-timezone';
import { PropTypes } from 'prop-types';
import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { showDialog } from '../../../core/actions';
import ContentTabs from '../../../core/components/ContentTabs';
import DetailHeader from '../../../core/components/DetailHeader';
import { EmbeddedUrlCarousel } from '../../../core/components/EmbeddedUrlCarousel';
import { hasRoleInOperatorGroup, RoleTypes } from '../../../core/components/Permission';
import ReadOnlyEditor from '../../../core/components/ReadOnlyEditor';
import { selectSession, selectSite } from '../../../store/authReducer';
import { getSiteModelYears, getSiteOperationTypes } from '../../settings/actions';
import { selectSiteModelYears, selectSiteOperationTypes } from '../../settings/reducer';
import { selectEmployees } from '../../employees/reducer';
import History from '../../taskDetail/components/History';
import Parts from '../../taskDetail/components/Parts';
import TaskDetailNotes from '../../taskDetail/components/TaskDetailNotes';
import { generateToastr } from '../../../core/toastMessages';
import { updateTaskCompletionStatus as updateTaskCompletionStatusAction } from '../../overview/actions';
import * as projectHelpers from '../WorkOrders/QuickTasks/helpers';
import DropdownMenuPortal from '../WorkOrders/QuickTasks/components/DropdownMenuPortal';

import {
  deleteEssentialTask,
  deleteTask,
  deleteQuickTask,
  deleteTaskInEdit,
  getTask,
  getQuickTask,
  getTaskLocations,
  getTaskPrefixes,
  saveEssentialTask,
  saveTask,
  addTask,
  fetchTasks,
  fetchQuickTasks,
} from '../../tasks/actions';
import { createIntervalDescription } from '../../tasks/helpers';
import { TaskType, Interval, TaskStatus } from '../../tasks/models';
import {
  selectTaskCompletions,
  selectTaskInEdit,
  selectTaskLocations,
  selectTaskPrefixes,
  selectCurrentEmployee,
} from '../../tasks/reducer';
import { PRIORITY_LABEL_TO_VALUE, PILL_COLORS } from '../WorkOrders/QuickTasks/constants';
import UserGroupPermissions, {
  editPermissionRefs,
} from '../../../core/components/UserGroupPermissions';
import { getApiStatus, toString } from '../../../core/utils';
import Loader from '../../../core/components/Loader';
import { DrawerControls } from '../../../core/components/Drawer';

export class TaskDetail extends Component {
  static propTypes = {
    intl: PropTypes.shape({}).isRequired,
    getTask: PropTypes.func,
    getQuickTask: PropTypes.func,
    saveTask: PropTypes.func,
    addTask: PropTypes.func,
    deleteQuickTask: PropTypes.func,
    saveEssentialTask: PropTypes.func,
    deleteTask: PropTypes.func,
    quickAddTask: PropTypes.bool,
    deleteEssentialTask: PropTypes.func,
    deleteTaskInEdit: PropTypes.func,
    showDialog: PropTypes.func,
    getTaskPrefixes: PropTypes.func,
    getTaskLocations: PropTypes.func,
    taskInEdit: PropTypes.shape({
      id: PropTypes.number.isRequired,
      interval: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      isEssentialTask: PropTypes.bool,
      description: PropTypes.string,
      modelYears: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          year: PropTypes.string,
        })
      ),
    }),
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
      goBack: PropTypes.func.isRequired,
    }).isRequired,
    session: PropTypes.shape({}),
    match: PropTypes.shape({
      params: PropTypes.shape({
        site: PropTypes.string,
        task: PropTypes.string,
      }),
    }),
    taskPrefixList: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.number,
        message: PropTypes.string,
      })
    ),
    taskLocationList: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.number,
        message: PropTypes.string,
        orderNumber: PropTypes.number,
      })
    ),
    siteOperationTypeList: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      })
    ),
    siteModelYearList: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        year: PropTypes.string,
      })
    ),
    getSiteModelYears: PropTypes.func,
    getSiteOperationTypes: PropTypes.func,
    quickAddTask: PropTypes.bool,
    hideDrawerControls: PropTypes.bool,
    employees: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        firstName: PropTypes.string,
        lastName: PropTypes.string,
      })
    ),
  };

  renderStatusPill = (text) => {
    const lower = String(text || '').toLowerCase();
    let bg = '#ccffe2';
    let fg = '#34c673';
    if (lower.includes('progress')) {
      bg = '#ffd1f7';
      fg = '#fc52df';
    } else if (lower.includes('to do') || lower.includes('todo')) {
      bg = '#d1d8ff';
      fg = '#5c75ff';
    } else if (lower.includes('planning')) {
      bg = '#ffe8d1';
      fg = '#f7830f';
    } else if (lower.includes('review')) {
      bg = '#f3e8ff';
      fg = '#7c3aed';
    } else if (lower.includes('backlog')) {
      bg = '#f1f5f9';
      fg = '#334155';
    } else if (lower.includes('completed')) {
      bg = '#ccffe2';
      fg = '#34c673';
    }
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 14px',
          borderRadius: 20,
          fontSize: 13,
          fontWeight: 600,
          lineHeight: 1,
          background: bg,
          color: fg,
        }}
      >
        <span style={{ whiteSpace: 'nowrap' }}>{text}</span>
        <span aria-hidden="true" style={{ fontSize: 11, color: 'inherit' }}>
          ▾
        </span>
      </span>
    );
  };

  getLatestCompletionId = (task) => {
    return projectHelpers.getLatestCompletionId(task);
  };

  getDisplayStatus = (task) => {
    if (this.state.localStatusOverride) {
      return projectHelpers.normalizeProjectStatus(this.state.localStatusOverride);
    }
    try {
      const text = this.getProjectStatusText(task);
      return projectHelpers.normalizeProjectStatus(text);
    } catch (e) {
      return 'Backlog';
    }
  };

  getEffectiveEmployeeId = (task) => {
    const explicit = this.props.currentEmployee;
    if (explicit) return explicit;
    if (task?.employeeId) return task.employeeId;
    if (task?.employee?.id) return task.employee.id;
    return null;
  };

  handleStatusUpdate = (task, nextLabel) => {
    if (!task || !nextLabel) return;
    const employeeId = this.getEffectiveEmployeeId(task);
    const site = Number(this.props.site);
    if (!employeeId || !site) {
      generateToastr(
        'error',
        this.props.intl.formatMessage({
          id: 'unableToChangeStatus',
          defaultMessage: 'Unable to change status',
        }),
        this.props.intl.formatMessage({
          id: 'selectEmployeeToUpdateStatus',
          defaultMessage: 'Select an employee before changing status.',
        })
      );
      return;
    }
    const apiStatus = projectHelpers.mapDisplayStatusToApiStatus(nextLabel);
    const taskId = task.id;
    this.setState({ localStatusOverride: nextLabel });
    try {
      this.props.updateTaskCompletionStatus({
        options: { siteId: site },
        taskIds: [taskId],
        status: apiStatus,
        employeeId,
        notes: null,
        callback: () => {
          try {
            this.props.fetchTasks();
            this.props.fetchQuickTasks();
          } catch (e) {}
        },
      });
    } catch (e) {}
  };

  handlePriorityUpdate = (task, label) => {
    if (!task || !label) return;
    const value = PRIORITY_LABEL_TO_VALUE[label] ?? null;
    if (value === null || value === undefined) return;
    this.setState({ localPriorityOverride: label });
    try {
      this.props.saveTask({
        task: {
          id: task.id,
          priority: value,
        },
      });
    } catch (e) {}
  };

  statusBtnRef = React.createRef();
  priorityBtnRef = React.createRef();

  state = {
    pendingEdit: false,
    pendingEditId: null,
    pendingNew: false,
    pendingNewId: null,
    statusMenuOpen: false,
    localStatusOverride: null,
    priorityMenuOpen: false,
    localPriorityOverride: null,
  };

  componentDidMount() {
    const id = this.props._taskId;
    if (this.props.quickAddTask) {
      this.props.getQuickTask(id);
    } else {
      this.props.getTask(id);
    }

    if (!this.props.taskPrefixList.length) {
      this.props.getTaskPrefixes();
    }
    if (!this.props.taskLocationList.length) {
      this.props.getTaskLocations();
    }
    if (hasRoleInOperatorGroup(this.props.session, RoleTypes.OperatorGroupAdmin)) {
      if (!this.props.siteModelYearList.length) {
        this.props.getSiteModelYears();
      }

      if (!this.props.siteOperationTypeList.length) {
        this.props.getSiteOperationTypes();
      }
    }
  }

  componentDidUpdate(prevProps) {
    const taskToEditChanged = !isEqual(this.props?._id, prevProps?._id);
    if (taskToEditChanged) {
      if (this.state.localStatusOverride || this.state.localPriorityOverride) {
        this.setState({ localStatusOverride: null, localPriorityOverride: null });
      }
      const id = this.props._taskId;
      if (this.props.quickAddTask) {
        this.props.getQuickTask(id);
      } else {
        this.props.getTask(id);
      }
    }
    if (!isEqual(this.props.taskInEdit, prevProps.taskInEdit)) {
      if (this.props.quickAddTask) {
        this.props.getQuickTask(this.props.taskInEdit.id);
      } else {
        this.props.getTask(this.props.taskInEdit.id);
      }
    }
    if (
      this.state.pendingEdit &&
      this.props.taskInEdit &&
      this.props.taskInEdit.id === this.state.pendingEditId
    ) {
      const isEssentialTask = this.props.taskInEdit?.isEssentialTask || false;
      const t = this.props.taskInEdit?.type;
      const isProject = t === TaskType.Project || t === 'Project';
      const formatMessage = this.props.intl.formatMessage;
      const title = isEssentialTask
        ? formatMessage({ id: 'editEssentialTask' })
        : formatMessage({ id: 'editTask' });

      const onSubmit = (task) => {
        const saveProps = { task };
        if (isEssentialTask) {
          this.props.saveEssentialTask(saveProps);
        } else {
          this.props.saveTask(saveProps);
        }
      };

      this.props.showDialog('UPSERT_TASK', {
        title,
        onSubmit,
        isEssentialTask,
        isNew: false,
        ...(isProject
          ? {
              completeQuickAddTask: true,
              taskDefaults: { quickAddTask: true, type: TaskType.Project, interval: Interval.Once },
            }
          : {}),
        modelYears: this.props.taskInEdit?.modelYears,
      });

      this.setState({ pendingEdit: false, pendingEditId: null });
    }
    if (this.props.isDeletionProcess && !prevProps.isDeletionProcess) {
      this.props.deleteTaskInEdit();
      this.props.removeSelectedTask();
    }

    if (
      this.state.pendingNew &&
      this.props.taskInEdit &&
      this.props.taskInEdit.id === this.state.pendingNewId
    ) {
      const isEssentialTask = this.props.taskInEdit?.isEssentialTask || false;
      const formatMessage = this.props.intl.formatMessage;
      const title = formatMessage({ id: 'addNewTask' });

      const onSubmit = (task) => {
        const saveProps = task;
        if (isEssentialTask) {
          this.props.saveEssentialTask(saveProps);
        } else {
          this.props.addTask(saveProps);
        }
      };

      this.props.showDialog('UPSERT_TASK', {
        title,
        onSubmit,
        isEssentialTask,
        isNew: true,
        completeQuickAddTask: true,
        modelYears: this.props.taskInEdit?.modelYears,
      });

      this.setState({ pendingNew: false, pendingNewId: null });
    }
  }

  editTask = () => {
    const sourceTask = this.props.taskInEdit || this.props;
    const id = sourceTask?.id || sourceTask?._taskId;
    const isEssentialTask = sourceTask?.isEssentialTask || false;
    const t = sourceTask?.type;
    const isProject = t === TaskType.Project || t === 'Project' || this.props.showEditForQuickAdd;
    const modelYears = sourceTask?.modelYears;
    const formatMessage = this.props.intl.formatMessage;
    const title = isEssentialTask
      ? formatMessage({ id: 'editEssentialTask' })
      : formatMessage({ id: 'editTask' });

    const onSubmit = (task) => {
      const saveProps = { task };
      if (this.props.quickAddTask) {
        this.props.addTask(task);
      } else {
        if (isEssentialTask) {
          this.props.saveEssentialTask(saveProps);
        } else {
          this.props.saveTask(saveProps);
        }
      }
    };

    if (this.props.quickAddTask && (!this.props.taskInEdit || this.props.taskInEdit.id !== id)) {
      this.props.getQuickTask(id);
      this.setState({ pendingEdit: true, pendingEditId: id });
      return;
    }

    this.props.showDialog('UPSERT_TASK', {
      title,
      onSubmit,
      isEssentialTask,
      isNew: false,
      ...(isProject
        ? {
            completeQuickAddTask: true,
            taskDefaults: { quickAddTask: true, type: TaskType.Project, interval: Interval.Once },
          }
        : {}),
      modelYears,
    });
  };

  newTask = () => {
    const id = this.props.taskInEdit?.id || this.props._taskId;
    const isEssentialTask = this.props.taskInEdit?.isEssentialTask || false;
    const formatMessage = this.props.intl.formatMessage;
    const title = isEssentialTask
      ? formatMessage({ id: 'editEssentialTask' })
      : formatMessage({ id: 'addNewTask' });

    const onSubmit = (task) => {
      const saveProps = { task };
      if (this.props.quickAddTask) {
        this.props.addTask(task);
        this.props.removeSelectedTask();
        generateToastr('success', 'Task created');
        const deletionParams = {
          id,
          callback: () => this.props.fetchQuickTasks(),
        };
        this.props.deleteQuickTask(deletionParams);
      } else {
        if (isEssentialTask) {
          this.props.saveEssentialTask(saveProps);
        } else {
          this.props.addTask(saveProps);
        }
      }
    };

    this.props.showDialog('UPSERT_TASK', {
      title,
      onSubmit,
      isEssentialTask,
      isNew: true,
      completeQuickAddTask: true,
      modelYears: this.props.taskInEdit.modelYears,
    });
  };

  deleteTask = () => {
    const { id, name, isEssentialTask } = this.props.taskInEdit;
    const deletionParams = {
      id,
      callback: () => this.props.fetchTasks(),
    };
    const quickTaskDeletionParams = {
      id,
      callback: () => this.props.fetchQuickTasks(),
    };

    const confirmProps = {
      title: this.props.intl.formatMessage({ id: 'archiveTask' }),
      text: this.props.intl.formatMessage({ id: 'taskArchiveConfirmation' }, { name }),
      onReady: this.props.quickAddTask
        ? () => {
            this.props.deleteQuickTask(quickTaskDeletionParams);
            this.props.removeSelectedTask();
          }
        : isEssentialTask
        ? () => this.props.deleteEssentialTask(deletionParams)
        : () => this.props.deleteTask(deletionParams),
    };

    this.props.showDialog('CONFIRM_DIALOG', confirmProps);
  };

  isProjectTask = (task) => {
    const typeVal = task?.type;
    return typeVal === TaskType.Project || typeVal === 'Project';
  };

  formatDate = (value) => {
    const timeZone =
      this.props.session?.timeZone || sessionStorage.getItem('timeZone') || moment.tz.guess();
    if (!value) {
      return this.props.intl.formatMessage({ id: 'notAvailable' });
    }
    try {
      return moment.tz(value, timeZone).format('MM/DD/YYYY');
    } catch (e) {
      return moment(value).format('MM/DD/YYYY');
    }
  };

  getProjectStatusText = (task) => {
    const status = task?.latestCompletion?.status;
    if (status === undefined || status === null) {
      return this.props.intl.formatMessage({ id: 'notAvailable' });
    }
    let raw = '';
    try {
      raw = toString(TaskStatus, status);
    } catch (e) {
      raw = String(status);
    }
    const lower = raw.toLowerCase();
    if (lower === 'pending') return 'Backlog';
    if (lower === 'snoozed') return 'In Progress';
    if (lower === 'completed') return 'Completed';
    return raw;
  };

  getPriorityText = (task) => {
    const priority = task?.priority;
    if (priority === undefined || priority === null || priority === '') {
      return this.props.intl.formatMessage({ id: 'notAvailable' });
    }
    const map = { 0: 'Low', 1: 'Medium', 2: 'High', 3: 'Urgent' };
    if (typeof priority === 'number') {
      return map[priority] ?? String(priority);
    }
    return String(priority);
  };

  renderPriorityPill = (label) => {
    const lower = String(label || '').toLowerCase();
    const colors = PILL_COLORS[lower] || PILL_COLORS.low;
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 14px',
          borderRadius: 999,
          fontSize: 13,
          fontWeight: 600,
          background: colors.bg,
          color: colors.fg,
        }}
      >
        <span style={{ whiteSpace: 'nowrap' }}>{label}</span>
        <span aria-hidden="true" style={{ fontSize: 11, color: 'inherit' }}>
          ▾
        </span>
      </span>
    );
  };

  getDisplayPriority = (task) => {
    if (this.state.localPriorityOverride) {
      return this.state.localPriorityOverride;
    }
    return this.getPriorityText(task);
  };

  getEmployeeName = (task) => {
    const employeeId = task?.employeeId;
    if (!employeeId) {
      return this.props.intl.formatMessage({ id: 'notAvailable' });
    }
    const employees = this.props.employees || [];
    const found = employees.find((e) => String(e.id) === String(employeeId));
    if (!found) {
      return this.props.intl.formatMessage({ id: 'notAvailable' });
    }
    return `${found.firstName} ${found.lastName}`;
  };

  createActionButtons = () => {
    const bypassGuard = this.props.forceQuickAddVisible && this.props.quickAddTask;
    if (
      !bypassGuard &&
      this.props?.taskInEdit?.groupId &&
      !hasRoleInOperatorGroup(this.props.session, RoleTypes.OperatorGroupAdmin)
    ) {
      return <div></div>;
    }

    return (
      <span>
        <button
          className="button primary inverted mr-2 ml-2"
          onClick={() => {
            this.deleteTask();
          }}
        >
          {this.props.intl.formatMessage({ id: 'delete' })}
        </button>
        {!this.props.quickAddTask && (
          <button
            className="button primary"
            onClick={() => {
              this.editTask();
            }}
          >
            {this.props.intl.formatMessage({ id: 'edit' })}
          </button>
        )}
        {this.props.quickAddTask &&
          (this.props.showEditForQuickAdd ? (
            <button
              className="button primary"
              onClick={() => {
                this.editTask();
              }}
            >
              {this.props.intl.formatMessage({ id: 'edit' })}
            </button>
          ) : (
            <button
              className="button primary"
              onClick={() => {
                this.newTask();
              }}
            >
              {this.props.intl.formatMessage({ id: 'createTask' })}
            </button>
          ))}
      </span>
    );
  };

  renderStatusSelector = (task) => {
    if (!this.isProjectTask(task)) return null;

    return (
      <div style={{ marginTop: 8 }}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <button
            type="button"
            className="btn btn-link p-0"
            ref={this.statusBtnRef}
            onClick={(e) => {
              e.stopPropagation();
              this.setState({ statusMenuOpen: !this.state.statusMenuOpen });
            }}
            aria-haspopup="listbox"
            aria-expanded={this.state.statusMenuOpen}
            style={{ textDecoration: 'none' }}
          >
            {this.renderStatusPill(this.getDisplayStatus(task))}
          </button>
          <DropdownMenuPortal
            anchorRef={this.statusBtnRef}
            open={this.state.statusMenuOpen}
            onClose={() => this.setState({ statusMenuOpen: false })}
            minWidth={160}
          >
            {['Backlog', 'Planning', 'In Progress', 'Review', 'Completed'].map((opt) => (
              <button
                key={opt}
                type="button"
                className={`dropdown-item${opt === this.getDisplayStatus(task) ? ' active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  this.setState({ statusMenuOpen: false }, () =>
                    this.handleStatusUpdate(task, opt)
                  );
                }}
              >
                {opt}
              </button>
            ))}
          </DropdownMenuPortal>
        </div>
      </div>
    );
  };

  renderPrioritySelector = (task) => {
    if (!this.isProjectTask(task)) return this.renderPriorityPill(this.getPriorityText(task));

    const options = ['Low', 'Medium', 'High', 'Urgent'];
    return (
      <div>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <button
            type="button"
            className="btn btn-link p-0"
            ref={this.priorityBtnRef}
            onClick={(e) => {
              e.stopPropagation();
              this.setState({ priorityMenuOpen: !this.state.priorityMenuOpen });
            }}
            aria-haspopup="listbox"
            aria-expanded={this.state.priorityMenuOpen}
            style={{ textDecoration: 'none' }}
          >
            {this.renderPriorityPill(this.getDisplayPriority(task))}
          </button>
          <DropdownMenuPortal
            anchorRef={this.priorityBtnRef}
            open={this.state.priorityMenuOpen}
            onClose={() => this.setState({ priorityMenuOpen: false })}
            minWidth={160}
          >
            {options.map((opt) => (
              <button
                key={opt}
                type="button"
                className={`dropdown-item${opt === this.getDisplayPriority(task) ? ' active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  this.setState({ priorityMenuOpen: false }, () =>
                    this.handlePriorityUpdate(task, opt)
                  );
                }}
              >
                {opt}
              </button>
            ))}
          </DropdownMenuPortal>
        </div>
      </div>
    );
  };

  getTitles = (task) => {
    const prefix = this.props.taskPrefixList.find(
      (taskPrefix) => taskPrefix.type === task.prefixId
    );
    const location = this.props.taskLocationList.find(
      (taskLocation) => taskLocation.type === task.locationId
    );
    const intl = this.props.intl;
    const notAvailable = intl.formatMessage({ id: 'notAvailable' });
    const isProject = this.isProjectTask(task);
    const nameValue = isProject && prefix ? `${prefix.message} ${task.name}` : task.name;
    const nameTitle = isProject
      ? {
          className: 'detail-name',
          key: 'detail-name',
          children: (
            <div className="detail-name">
              {/* <label>{intl.formatMessage({ id: 'name' })}</label> */}
              <div>{nameValue}</div>
              {this.renderStatusSelector(task)}
            </div>
          ),
        }
      : {
          className: 'detail-name',
          label: intl.formatMessage({ id: 'name' }),
          content: nameValue,
        };

    const titles = [
      nameTitle,
      {
        className: 'detail-id',
        label: intl.formatMessage({ id: 'id' }),
        content: task.id,
      },
    ];

    if (!isProject) {
      titles.push({
        className: 'detail-interval',
        label: intl.formatMessage({ id: 'interval' }),
        content: createIntervalDescription(task, intl.formatMessage),
      });
    }

    if (isProject) {
      titles.push({
        label: intl.formatMessage({ id: 'createdOn' }),
        content: this.formatDate(task.createdAt || task.start),
      });
      const dueSource = task.end ?? null;
      titles.push({
        label: intl.formatMessage({ id: 'dueDate' }),
        content: dueSource ? this.formatDate(dueSource) : notAvailable,
      });
    } else {
      titles.push({
        label: intl.formatMessage({ id: 'start' }),
        content: this.formatDate(task.start),
      });
      titles.push({
        label: intl.formatMessage({ id: 'end' }),
        content: task.end ? this.formatDate(task.end) : notAvailable,
      });
    }

    titles
      .push
      // {
      //   label: intl.formatMessage({ id: 'taskLocation' }),
      //   content: location ? location.message : notAvailable,
      // }
      ();

    if (isProject) {
      titles.push(
        {
          label: intl.formatMessage({ id: 'priority' }),
          content: this.renderPrioritySelector(task),
        },
        {
          label: intl.formatMessage({ id: 'assignee' }),
          content: this.getEmployeeName(task),
        }
      );
    }

    return titles;
  };

  render() {
    const task = this.props.taskInEdit || {};
    const intl = this.props.intl;
    const instructionsLabel = this.isProjectTask(task)
      ? intl.formatMessage({ id: 'description' })
      : intl.formatMessage({ id: 'instructions' });

    const baseComponents = {
      [instructionsLabel]: (
        <ReadOnlyEditor text={task.description === undefined ? '' : task.description} />
      ),
      Videos: <EmbeddedUrlCarousel urls={task.videos} isVideo={true} />,
      History: <History task={task} />,
      Notes: <TaskDetailNotes task={task} />,
    };

    const components = task.isEssentialTask
      ? baseComponents
      : {
          ...baseComponents,
          Parts: <Parts task={task} site={this.props.match.params.site} />,
        };
    return this.props.isEditingProcess || this.props.isLoadingTask ? (
      <Loader />
    ) : (
      <div
        data-testid="todays-task-in-detail"
        className="todays-task-detail container-fluid no-padding"
      >
        <section className="page-details-content no-padding">
          {!this.props.hideDrawerControls && (
            <>
              <div className="flex space-between">
                <div>
                  <DrawerControls
                    closeFunction={() => this.props.removeSelectedTask()}
                    maximized={this.props.taskInEditExpanded}
                    maximizeFunction={() => this.props.setTaskInEditExpanded()}
                  />
                </div>
                <UserGroupPermissions itemRef={editPermissionRefs.task}>
                  <div className="align-right">{this.createActionButtons()}</div>
                </UserGroupPermissions>
              </div>
              <hr />
            </>
          )}
          {task.isEssentialTask && (
            <span className="badge badge-secondary mb-2">
              {intl.formatMessage({ id: 'essentialTask' })}
            </span>
          )}
          <DetailHeader titles={this.getTitles(task)} buttonClass={'mt-2'} />
          <ContentTabs components={components} scrollable={true} />
        </section>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  taskInEdit: selectTaskInEdit(state),
  taskCompletions: selectTaskCompletions(state),
  session: selectSession(state),
  taskPrefixList: selectTaskPrefixes(state),
  taskLocationList: selectTaskLocations(state),
  siteModelYearList: selectSiteModelYears(state),
  siteOperationTypeList: selectSiteOperationTypes(state),
  employees: selectEmployees(state),
  site: Number(selectSite(state)),
  currentEmployee: selectCurrentEmployee(state),
  isLoadingTask: getApiStatus(state, 'GET_TASK'),
  isDeletionProcess: getApiStatus(state, 'DELETE_TASK'),
  isEditingProcess: getApiStatus(state, 'SAVE_TASK'),
});

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      fetchTasks,
      fetchQuickTasks,
      getTask,
      getQuickTask,
      deleteTask,
      deleteQuickTask,
      deleteEssentialTask,
      deleteTaskInEdit,
      saveTask,
      addTask,
      saveEssentialTask,
      showDialog,
      getTaskPrefixes,
      getTaskLocations,
      getSiteModelYears,
      getSiteOperationTypes,
      updateTaskCompletionStatus: updateTaskCompletionStatusAction,
    },
    dispatch
  );
};

const enhance = compose(connect(mapStateToProps, mapDispatchToProps), injectIntl);

export default enhance(TaskDetail);
